import { dayjs } from '@/extendedDayjs';
import { User } from './types';
import { Duration } from 'dayjs/plugin/duration';

export const Today = dayjs();
export const TodayUTC = dayjs.utc();

export const calculateElapsedSoakTime: (soakStartTime: Date, now: dayjs.Dayjs) => Duration = (soakStartTime, now) => {
    const soakStartMoment = dayjs(soakStartTime);
    const difference = dayjs.duration(now.diff(soakStartMoment));

    return difference;
};

export const calculateSoakTimeRemaining: (
    minimumSoakInSeconds: number,
    soakStartTime: Date,
    now: dayjs.Dayjs,
) => Duration = (minimumSoakInSeconds, soakStartTime, now) => {
    const soakStartedAt = dayjs(soakStartTime);
    const minimumSoakDurationInSeconds = dayjs.duration(minimumSoakInSeconds, 's');
    const currentSoakDurationSeconds = dayjs.duration(now.diff(soakStartedAt)).asSeconds();

    if (currentSoakDurationSeconds > minimumSoakDurationInSeconds.asSeconds()) {
        return dayjs.duration(0);
    } else {
        const soakCompletedAt = soakStartedAt.add(minimumSoakDurationInSeconds);
        const timeRemaining = dayjs.duration(soakCompletedAt.diff(now));

        return timeRemaining;
    }
};

export const userIsNewUser: (user: User, now: string) => boolean = (user, now) => {
    const nowMoment = dayjs(now);
    const momentCreatedAt = dayjs(user.createdAt);
    const isNewUser = nowMoment.diff(momentCreatedAt, 'days') < 7;

    return isNewUser;
};

export const abbreviateTimeUnitAndRemoveLeadingSpace: (timeString: string) => string = (timeString) => {
    let formattedString = timeString;
    formattedString = formattedString
        .replace('a few seconds', '< 1m') // Moment returns "a few seconds ago" for 0-44s
        .replace('a ', '1 ') // Moment returns "a minute", "a day" etc
        .replace('an ', '1 ') // Moment return "an hour"
        .replace('s', '')
        .replace(' second', 's')
        .replace(' minute', 'm')
        .replace(' hour', 'h')
        .replace(' day', 'd')
        .replace(' week', 'w')
        .replace(' year', 'y');
    return formattedString;
};

export const formatShortDuration = (milliseconds: number): string => {
    const duration = dayjs.duration(Math.max(0, milliseconds));
    const days = Math.floor(duration.asDays());

    if (days > 0) {
        return `${days} d ${duration.hours()} hr`;
    }
    if (duration.hours() > 0) {
        return `${duration.hours()} hr ${duration.minutes()} min`;
    }
    if (duration.minutes() > 0) {
        return `${duration.minutes()} min`;
    }
    return '< 1 min';
};

export const convertTo24HourFormat = (format: string): string => {
    return format.replace(/h/g, 'H').replace(/ ?a(?=\s|$)/gi, '');
};

export enum DatetimeFormat {
    ShortMonthDateTime = 'MMM Do [at] h:mm a', // Jan 1st at 2:30 pm
    ShortMonthDateTime2 = 'MMM Do, h:mm a', // Jan 1st, 2:30 pm
    MonthAbbreviatedDateTime = 'MMM Do [at] h:mm a', // Jan 1st at 2:30 pm
    DayAbbreviatedMonthDateYearTime = 'ddd, MMM Do YYYY, h:mm:ss a', // Mon, Jan 1st 2024, 2:30:45 pm
    DayAbbreviatedMonthDateYearTimeAbbreviated = 'ddd, MMM Do, YYYY [at] h:mm a', // Mon, Jan 1st, 2024 at 2:30 pm
    DayMonthDateYear = 'dddd, MMM Do YYYY', // Monday, Jan 1st 2024
    ShortMonthDateYear = 'MMM D, YYYY', // Jan 1, 2024
    DayMonthDate = 'dddd, MMMM Do', // Monday, January 1st
    ShortDayMonthDate = 'ddd, MMM Do', // Mon, Jan 1st
    ShortDayMonthDateNonOrdinal = 'ddd, MMM D', // Mon, Jan 1
    MonthDate = 'MMMM Do', // January 1st
    ShortMonthDate = 'MMM D', // Jan 1
    ShortMonthDateAppendix = 'MMM Do', // Jan 1st
    TimeShortDate = 'h:mm A [on] MMM Do', // 2:30 PM on Jan 1st
    LongMonthDateTime = 'MMMM Do, YYYY [at] h:mm A', // January 28th, 2021 at 2:14 PM
    ShortTime = 'h:mm a', // 2:30 pm
    Hour = 'h A', // 2 PM
}

export type TimeFormat =
    | 'full'
    | 'short'
    | 'mobile'
    | 'fullYearAtTime'
    | 'timezone'
    | 'shortHourOnly'
    | 'shortHourOnlyWithoutTimezone'
    | 'hourOnly'
    | 'shortWithComma'
    | 'abbreviatedYear'
    | 'dateOnly'
    | 'shortHour'
    | 'shortWithTimezone'
    | 'fullWithoutTimezone'
    | 'fullWithTimezone'
    | 'shortMonthDate'
    | 'shortMonthDateWithTime'
    | 'shortMonthDateWithTimeZone'
    | 'shortWithYearAndComma'
    | 'dayMonthDateYear'
    | 'longMonthDateYearTime';

export const getLocaleDateTimeFormat = (format: TimeFormat = 'full', use24HourTimeFormat: boolean = false): string => {
    const testDate = new Date(2020, 11, 31);
    const formattedDate = testDate.toLocaleDateString();
    const numbers = formattedDate.match(/\d+/g);
    const isMonthFirst = numbers && numbers[0] === '12';

    let dateFormat: string;
    switch (format) {
        case 'mobile':
            dateFormat =
                isMonthFirst ?
                    'M/D, h:mm a' // 7/14, 2:13 pm
                :   'D/M, h:mm a'; // 14/7, 2:13 pm
            break;
        case 'short':
            dateFormat =
                isMonthFirst ?
                    'MM/DD [at] h:mm A' // 12/28 at 2:14 PM
                :   'DD/MM [at] h:mm A'; // 28/12 at 2:14 PM
            break;
        case 'shortWithTimezone':
            dateFormat =
                isMonthFirst ?
                    'MM/DD [at] h:mm A z' // 12/28 at 2:14 PM PST
                :   'DD/MM [at] h:mm A z'; // 28/12 at 2:14 PM PST
            break;
        case 'shortWithComma':
            dateFormat =
                isMonthFirst ?
                    'MM/DD, h:mm A' // 12/28, 2:13 PM
                :   'DD/MM, h:mm A'; // 28/12, 2:13 PM
            break;
        case 'shortWithYearAndComma':
            dateFormat =
                isMonthFirst ?
                    'MM/DD/YY, h:mm A' // 12/28/21, 2:14 PM
                :   'DD/MM/YY, h:mm A'; // 28/12/21, 2:14 PM
            break;
        case 'abbreviatedYear':
            dateFormat =
                isMonthFirst ?
                    'M/D/YYYY h:mm A' // 7/14/2021 2:13 PM
                :   'D/M/YYYY h:mm A'; // 14/7/2021 2:13 PM
            break;
        case 'fullYearAtTime':
            dateFormat =
                isMonthFirst ?
                    'MM/DD/YYYY [at] h:mm A' // 12/28/2021 at 2:14 PM
                :   'DD/MM/YYYY [at] h:mm A'; // 28/12/2021 at 2:14 PM
            break;
        case 'timezone':
            dateFormat =
                isMonthFirst ?
                    'MM/DD/YY, h:mm A z' // 12/28/21, 2:14 PM PST
                :   'DD/MM/YY, h:mm A z'; // 28/12/21, 2:14 PM PST
            break;
        case 'dateOnly':
            dateFormat =
                isMonthFirst ?
                    'M/D/YYYY' // 7/14/2021
                :   'D/M/YYYY'; // 14/7/2021
            break;
        case 'shortHour':
            dateFormat =
                isMonthFirst ?
                    'MM/DD [at] ha' // 01/15 at 2pm
                :   'DD/MM [at] ha'; // 15/01 at 2pm
            break;
        case 'shortHourOnly':
            dateFormat = 'h:mm a z'; // 2:14 pm PST
            break;
        case 'shortHourOnlyWithoutTimezone':
            dateFormat = 'h:mm a'; // 2:14 pm
            break;
        case 'hourOnly':
            dateFormat = 'h a z'; // 2 pm PST
            break;
        case 'fullWithoutTimezone':
            dateFormat = 'ddd, MMM Do YYYY, h:mm:ss a';
            break;
        case 'fullWithTimezone':
            dateFormat = 'ddd, MMM Do YYYY, h:mm:ss a z';
            break;
        case 'shortMonthDateWithTime':
            dateFormat = 'MMM Do, h:mm a';
            break;
        case 'shortMonthDateWithTimeZone':
            dateFormat = 'MMM Do, h:mm a z'; // Jan 1st, 2:30 pm PST
            break;
        case 'shortMonthDate':
            dateFormat = 'MMM D';
            break;
        case 'dayMonthDateYear':
            dateFormat = 'dddd, MMM D';
            break;
        case 'longMonthDateYearTime':
            dateFormat = 'MMMM D, YYYY, h:mm A'; // September 8, 2024, 9:42 AM
            break;
        default:
            dateFormat =
                isMonthFirst ?
                    'MM/DD/YY [at] h:mm A' // 12/28/21 at 2:14 PM
                :   'DD/MM/YY [at] h:mm A'; // 28/12/21 at 2:14 PM
            break;
    }

    return use24HourTimeFormat ? convertTo24HourFormat(dateFormat) : dateFormat;
};

export const getTimezoneDiff = (baseTimezone: string, targetTimezone: string): string => {
    const baseDateWithTimezone = dayjs().tz(baseTimezone || 'UTC');
    const timezoneDiffFormat = `([${targetTimezone}]Z)`;
    return baseDateWithTimezone.format(timezoneDiffFormat);
};

export const formatDateWithTimezone = (
    date: dayjs.Dayjs | Date | string | null | undefined,
    formatType: TimeFormat,
    timezone: string,
    use24HourTimeFormat: boolean,
): string => {
    if (!date) return '';

    const formatString = getLocaleDateTimeFormat(formatType, use24HourTimeFormat);
    const tz = timezone || 'UTC';
    const d = dayjs(date);
    const dateWithTz = typeof d.tz === 'function' ? d.tz(tz) : d;

    try {
        return dateWithTz.format(formatString);
    } catch {
        return dateWithTz.format(formatString.replace(/ z/g, ''));
    }
};

export const MINUTES_PER_HOUR = 60;

export const formatDelayMinutes = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
    const minutes = totalMinutes % MINUTES_PER_HOUR;
    return [hours > 0 && `${hours}h`, minutes > 0 && `${minutes}m`].filter(Boolean).join(' ') || '0m';
};
